import { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import type {
  BoardroomEmailCapture,
  BoardroomEmailCaptureRow,
  BoardroomEmailSignup,
  EmailSpyConnection,
} from '../../../types';
import DataTable from './DataTable';
import type { Column } from './tableColumns';

/**
 * Email capture: a row per company, how it asks for an email address, who provides the
 * form, and the pages it was seen on. Read off the pages already fetched for each company,
 * so it fills in as their sites are read; nothing here starts a fetch.
 *
 * "Sign up all" signs up to each company's form with a spy address from the email spy (in the
 * mailer), so their emails land there. A company is signed up once; new ones are picked up as
 * their forms are found. See backend boardroom/email_signups.py.
 *
 * Each room sends its sign-ups to one mailer org (the default connection unless the room picks
 * another). Sign-ups are kept per mailer org, so a competitor in two rooms tied to different orgs
 * is signed up in each.
 */

const SETTINGS_URL = '/settings?tab=connections&sub=email-spy';
const ORG_HINT =
  'Which mailer org gets this room\'s sign-ups. Sign-ups are kept per mailer org, so the same competitor in rooms tied to different orgs is signed up in each.';

type Row = BoardroomEmailCaptureRow;

/** The url without its scheme, for a narrow cell. */
export function shortUrl(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') || url;
}

/** The provider cell. A form we could not tie to a provider says so. */
export function providerText(row: Row): string {
  return row.providers.length ? row.providers.join(', ') : 'Unknown';
}

const MAX_TRIES = 3;

function day(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : '';
}

/** How much mail a signed-up address has had, e.g. ", 5 emails". Empty until checked. */
function mailText(s: BoardroomEmailSignup): string {
  if (s.mail_count == null) return '';
  if (s.mail_count === 0) return ', no mail yet';
  return `, ${s.mail_count} email${s.mail_count === 1 ? '' : 's'}`;
}

/** One sign-up, in a few words. */
export function signupText(s: BoardroomEmailSignup | undefined): string {
  if (!s) return 'Not yet';
  switch (s.status) {
    case 'waiting':
      return s.available_at && !s.address ? `Waits until ${day(s.available_at)}` : 'Waiting';
    case 'working':
      return 'Signing up...';
    case 'signed_up':
      return `Signed up ${day(s.signed_up_at)}${mailText(s)}`;
    case 'done_by_hand':
      return `Done by hand ${day(s.signed_up_at)}${mailText(s)}`;
    case 'needs_person':
      return `Needs you: ${s.note || 'the robot could not finish'}`;
    case 'failed':
      return `Failed: ${s.note || 'unknown'}${s.tries < MAX_TRIES ? ' (will try again)' : ''}`;
    default:
      return s.status;
  }
}

/** What the form asked for, from the sign-up's notes. */
/** The made-up person to type into a form by hand, e.g. "Jane Harper, Marketing Manager, Harper & Lane, United States". */
export function personaText(s: BoardroomEmailSignup | null | undefined): string {
  const p = s?.persona;
  if (!p) return '';
  const name = [p.first_name, p.last_name].filter(Boolean).join(' ');
  return [name, p.job_title, p.company, p.country].filter(Boolean).join(', ');
}

export function askedText(s: BoardroomEmailSignup | undefined): string {
  const fields = s?.fields_asked ?? [];
  return fields
    .map(f => f.label || f.name)
    .filter(Boolean)
    .join(', ');
}

export default function EmailCaptureReport({
  data: initial,
  onOpen,
  roomName,
  boardroomId,
}: {
  data: BoardroomEmailCapture;
  onOpen: (itemId: number) => void;
  roomName?: string;
  /** The room, so "Sign up all" knows where it is. Without it the button is hidden. */
  boardroomId?: number;
}) {
  const LINK = 'text-indigo-600 dark:text-indigo-400 hover:underline text-left';
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; bad?: boolean; settings?: boolean } | null>(null);
  useEffect(() => setData(initial), [initial]);

  // The mailer connections, for the "Mailer org" pick. null until loaded.
  const [connections, setConnections] = useState<EmailSpyConnection[] | null>(null);
  useEffect(() => {
    if (boardroomId == null) return;
    let live = true;
    api
      .listEmailSpyConnections()
      .then(got => live && setConnections(got))
      .catch(() => live && setConnections(null));
    return () => {
      live = false;
    };
  }, [boardroomId]);

  const refresh = useCallback(async () => {
    if (boardroomId == null) return;
    try {
      setData(await api.getBoardroomReport<BoardroomEmailCapture>(boardroomId, 'email_capture'));
    } catch {
      // The next poll tries again.
    }
  }, [boardroomId]);

  // While sign-ups are running, check back every few seconds.
  const running = data.rows.some(r => (r.signups ?? []).some(s => s.status === 'working'));
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(refresh, 10000);
    return () => clearInterval(timer);
  }, [running, refresh]);

  const signUpAll = async () => {
    if (boardroomId == null) return;
    setBusy(true);
    setMessage(null);
    try {
      const got = await api.startBoardroomEmailSignups(boardroomId);
      const failed = got.errors.length ? ` ${got.errors.length} could not get an address.` : '';
      setMessage({ text: `Started ${got.started} sign-up${got.started === 1 ? '' : 's'}.${failed}`, bad: !!failed });
      await refresh();
    } catch (err) {
      const text = err instanceof Error ? err.message : 'Could not start the sign-ups';
      setMessage({ text, bad: true, settings: /Connections|API key|mailer/i.test(text) });
    } finally {
      setBusy(false);
    }
  };

  const retryAll = async () => {
    if (boardroomId == null) return;
    setBusy(true);
    setMessage(null);
    try {
      const got = await api.retryBoardroomEmailSignups(boardroomId);
      setMessage({ text: `Trying ${got.started} again.` });
      await refresh();
    } catch (err) {
      const text = err instanceof Error ? err.message : 'Could not try them again';
      setMessage({ text, bad: true, settings: /Connections|API key|mailer/i.test(text) });
    } finally {
      setBusy(false);
    }
  };

  const pickConnection = async (value: string) => {
    if (boardroomId == null) return;
    setMessage(null);
    try {
      await api.setBoardroomEmailSpyConnection(boardroomId, value ? Number(value) : null);
      await refresh();
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Could not change the mailer org', bad: true });
    }
  };

  const act = async (signup: BoardroomEmailSignup, action: 'retry' | 'done') => {
    if (boardroomId == null) return;
    try {
      await api.actOnBoardroomEmailSignup(boardroomId, signup.id, action);
      await refresh();
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'That did not work', bad: true });
    }
  };

  const rows = data.rows.filter(r => r.status === 'asks');
  const first = (r: Row) => (r.signups ?? []).find(s => s.which === 1);
  const second = (r: Row) => (r.signups ?? []).find(s => s.which === 2);
  const due = data.signup_due ?? 0;
  const retry = data.signup_retry ?? 0;
  const defaultConnection = connections?.find(c => c.is_default);
  const chosenId = data.email_spy?.chosen ? String(data.email_spy.id) : '';
  const columns: Column<Row>[] = [
    {
      key: 'title',
      label: 'Company',
      width: 14,
      always: true,
      value: r => r.title,
      cell: r => (
        <button type="button" onClick={() => onOpen(r.id)} className={LINK}>
          {r.title}
        </button>
      ),
    },
    { key: 'provider', label: 'Provider', width: 10, value: r => providerText(r) },
    {
      key: 'pages',
      label: 'Found on',
      width: 22,
      value: r => r.pages.map(p => p.url).join(' '),
      cell: r => (
        <ul className="space-y-0.5">
          {r.pages.map(p => (
            <li key={p.url}>
              <a href={p.url} target="_blank" rel="noopener noreferrer" className={LINK}>
                {shortUrl(p.url)}
              </a>
              <span className="text-gray-500 dark:text-gray-400"> · {p.where}</span>
            </li>
          ))}
        </ul>
      ),
    },
    { key: 'kinds', label: 'Kind', width: 9, value: r => r.kinds.join(', ') },
    {
      key: 'offer',
      label: 'Offer',
      width: 9,
      value: r => [r.offer, r.form_appeal?.appeal].filter(Boolean).join(' '),
      cell: r => (
        <div className="space-y-0.5">
          <div>{r.offer}</div>
          {r.form_appeal?.appeal && (
            <div className="text-gray-500 dark:text-gray-400">
              Appeal: {r.form_appeal.appeal}
              {r.form_appeal.pull && ` (${r.form_appeal.pull})`}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'signed_up',
      label: 'Signed up',
      width: 14,
      value: r => signupText(first(r)),
      cell: r => {
        const s = first(r);
        const later = second(r);
        if (!r.form && !s) return <span className="text-gray-500 dark:text-gray-400">No form to fill</span>;
        return (
          <div className="space-y-0.5">
            <div>{signupText(s)}</div>
            {s && (s.status === 'needs_person' || s.status === 'failed') && personaText(s) && (
              <div className="text-gray-500 dark:text-gray-400">Sign up as {personaText(s)}</div>
            )}
            {s && (s.status === 'needs_person' || s.status === 'failed') && boardroomId != null && (
              <div className="flex gap-2">
                {s.page_url && (
                  <a href={s.page_url} target="_blank" rel="noopener noreferrer" className={LINK}>
                    Open form
                  </a>
                )}
                {s.address && (
                  <button type="button" className={LINK} onClick={() => navigator.clipboard?.writeText(s.address || '')}>
                    Copy address
                  </button>
                )}
                <button type="button" className={LINK} onClick={() => act(s, 'retry')}>
                  Try again
                </button>
                <button type="button" className={LINK} onClick={() => act(s, 'done')}>
                  Mark done
                </button>
              </div>
            )}
            {later && <div className="text-gray-500 dark:text-gray-400">Second address: {signupText(later)}</div>}
          </div>
        );
      },
    },
    {
      key: 'asked',
      label: 'Asked for',
      width: 12,
      value: r => askedText(first(r)),
      cell: r => {
        const s = first(r);
        return (
          <div className="space-y-0.5">
            <div>{askedText(s)}</div>
            {s?.download_url && (
              <a href={s.download_url} target="_blank" rel="noopener noreferrer" className={LINK}>
                What they gave
              </a>
            )}
          </div>
        );
      },
    },
  ];
  return (
    <div className="min-w-0 space-y-2 text-xs" data-testid="email-capture">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Forms found for {data.asking} of {data.companies} companies. No forms found for {data.asks_nothing},
        and {data.unread} have not had a page read since we started looking for forms. This fills in as their sites are
        read.
      </p>
      {boardroomId != null && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400">
            Signed up for {data.signed_up ?? 0} of {data.asking}.
            {(data.signup_needs_person ?? 0) > 0 && ` ${data.signup_needs_person} need you.`}
            {due > 0 && ` ${due} ready to sign up.`}
            {(data.signup_no_mail ?? 0) > 0 && ` ${data.signup_no_mail} no mail after 2 days.`}
          </span>
          {connections && connections.length > 0 && (
            <label className="text-gray-500 dark:text-gray-400" title={ORG_HINT}>
              Mailer org:{' '}
              <select
                value={chosenId}
                onChange={e => pickConnection(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded px-1 py-0.5 text-xs text-gray-700 dark:text-gray-200"
              >
                <option value="">Default ({defaultConnection?.name ?? 'none'})</option>
                {connections.map(c => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                    {c.organization_name ? ` (${c.organization_name})` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          {connections && connections.length === 0 && !message?.settings && (
            <a href={SETTINGS_URL} className={LINK}>
              Open Email spy settings
            </a>
          )}
          {due > 0 && (
            <button
              type="button"
              onClick={signUpAll}
              disabled={busy}
              className="px-2 py-1 bg-indigo-600 text-white rounded disabled:opacity-50"
            >
              {busy ? 'Starting...' : `Sign up all (${due})`}
            </button>
          )}
          {retry > 0 && (
            <button
              type="button"
              onClick={retryAll}
              disabled={busy}
              title="Failed, stopped at a robot check, or no mail after 2 days"
              className="px-2 py-1 border border-indigo-600 text-indigo-700 dark:text-indigo-300 rounded disabled:opacity-50"
            >
              {`Try again (${retry})`}
            </button>
          )}
          {message && (
            <span className={message.bad ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'}>
              {message.text}
              {message.settings && (
                <>
                  {' '}
                  <a href={SETTINGS_URL} className={LINK}>
                    Open Email spy settings
                  </a>
                </>
              )}
            </span>
          )}
        </div>
      )}
      {data.providers.length > 0 && (
        <p className="text-gray-500 dark:text-gray-400">
          {data.providers
            .slice(0, 6)
            .map(p => `${p.name} ${p.companies}`)
            .join(' · ')}
        </p>
      )}
      <DataTable
        table="email_capture"
        room={roomName}
        file="email-capture"
        columns={columns}
        rows={rows}
        rowKey={r => `capture-row-${r.id}`}
        empty="No company here asks for an email on the pages read so far."
      />
    </div>
  );
}
